import { NewsArticle, NewsCategory, NewsSource } from '@/types/news';

// Default list of music business news sources 
export const newsSources: NewsSource[] = [
  {
    id: 'billboard',
    name: 'Billboard',
    iconUrl: 'https://www.billboard.com/wp-content/themes/vip/pmc-billboard-2021/assets/app/icons/favicon-32x32.png',
    url: 'https://www.billboard.com/business/',
    enabled: true
  },
  {
    id: 'mbw',
    name: 'Music Business Worldwide',
    iconUrl: 'https://www.musicbusinessworldwide.com/wp-content/uploads/2016/06/mbw-favicon-194x194.png',
    url: 'https://www.musicbusinessworldwide.com/',
    enabled: true
  },
  {
    id: 'musicbiz',
    name: 'Music Business Association',
    iconUrl: 'https://musicbiz.org/wp-content/uploads/2014/03/Favicon3.png',
    url: 'https://musicbiz.org/news/',
    enabled: true
  },
  {
    id: 'hypebot',
    name: 'Hypebot',
    url: 'https://www.hypebot.com/',
    enabled: true
  },
  {
    id: 'musicAlly',
    name: 'Music Ally',
    url: 'https://musically.com/',
    enabled: true
  },
  {
    id: 'digitalmusicNews',
    name: 'Digital Music News',
    url: 'https://www.digitalmusicnews.com/',
    enabled: true
  }
];

// In a real implementation, this would fetch from a news API
// For now, we'll simulate with mock data
export const fetchNewsArticles = async (
  categories?: NewsCategory[],
  sources?: string[],
  page: number = 1,
  itemsPerPage: number = 10
): Promise<{ articles: NewsArticle[], total: number }> => {
  console.log('Fetching news with filters:', { categories, sources, page, itemsPerPage });
  
  // Simulate API request delay
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // Filter mock articles based on categories and sources
  let filteredArticles = [...mockArticles];
  
  if (categories && categories.length > 0) {
    filteredArticles = filteredArticles.filter(article => 
      categories.includes(article.category)
    );
  }
  
  if (sources && sources.length > 0) {
    filteredArticles = filteredArticles.filter(article => 
      sources.includes(article.source)
    );
  }
  
  // Calculate pagination
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedArticles = filteredArticles.slice(startIndex, endIndex);
  
  return {
    articles: paginatedArticles,
    total: filteredArticles.length
  };
};

// Mock article data
export const mockArticles: NewsArticle[] = [
  {
    id: '1',
    title: 'Streaming Revenue Reaches All-Time High in 2024',
    description: 'Industry reports show streaming revenue has increased by 15% year-over-year, reaching an all-time high in the first quarter of 2024.',
    url: 'https://www.billboard.com/business/streaming/streaming-revenue-all-time-high-2024-q1-1235731098/',
    imageUrl: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?q=80&w=800',
    source: 'billboard',
    publishDate: new Date('2024-05-01'),
    category: 'business',
    readTime: 5
  },
  {
    id: '2',
    title: 'Major Label Signs Deal with AI Music Generation Platform',
    description: 'One of the Big Three labels has signed a groundbreaking deal with an AI music generation platform, allowing artists to leverage AI tools for composition while ensuring proper rights management.',
    url: 'https://www.musicbusinessworldwide.com/ai-music-deal-major-label-12489273/',
    imageUrl: 'https://images.unsplash.com/photo-1559526324-593bc073d938?q=80&w=800',
    source: 'mbw',
    publishDate: new Date('2024-04-28'),
    category: 'technology',
    readTime: 7
  },
  {
    id: '3',
    title: 'Music Publishing Revenues Grow by 8.5% in 2023',
    description: 'Global music publishing revenues have grown by 8.5% in 2023, according to a new report, with digital and streaming accounting for over 70% of all revenue.',
    url: 'https://www.musicbusinessworldwide.com/publishing-revenues-grow-2023-report/',
    imageUrl: 'https://images.unsplash.com/photo-1507838153414-b4b713384a76?q=80&w=800',
    source: 'mbw',
    publishDate: new Date('2024-04-25'),
    category: 'publishing',
    readTime: 6
  },
  {
    id: '4',
    title: 'New Copyright Legislation Proposed to Better Protect Songwriters',
    description: 'A bipartisan bill has been introduced that aims to update copyright laws to better protect songwriters and composers in the digital age.',
    url: 'https://www.billboard.com/business/legal/copyright-legislation-songwriters-1235730987/',
    imageUrl: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=800',
    source: 'billboard',
    publishDate: new Date('2024-04-22'),
    category: 'legal',
    readTime: 8
  },
  {
    id: '5',
    title: 'Indie Artists Seeing Higher Revenue Share from Music Subscription Services',
    description: 'New data shows that independent artists are receiving a growing percentage of streaming revenue, as DSPs implement new payout models.',
    url: 'https://www.hypebot.com/indie-artists-revenue-share-growing-2024/',
    imageUrl: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?q=80&w=800',
    source: 'hypebot',
    publishDate: new Date('2024-04-20'),
    category: 'business',
    readTime: 4
  },
  {
    id: '6',
    title: 'Concert Promoters Report Record-Breaking Ticket Sales',
    description: 'Live music is booming in 2024, with major concert promoters reporting record-breaking ticket sales as post-pandemic demand continues to surge.',
    url: 'https://www.musicbiz.org/news/concert-promoters-record-ticket-sales-2024/',
    imageUrl: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?q=80&w=800',
    source: 'musicbiz',
    publishDate: new Date('2024-04-18'),
    category: 'events',
    readTime: 5
  },
  {
    id: '7',
    title: 'New Blockchain Platform Promises Faster Royalty Payments for Artists',
    description: 'A new blockchain-based platform claims to reduce royalty payment timelines from months to days, offering transparency and efficiency improvements.',
    url: 'https://www.digitalmusicnews.com/blockchain-royalty-platform-artists-1247384/',
    imageUrl: 'https://images.unsplash.com/photo-1639762681057-408e52192e55?q=80&w=800',
    source: 'digitalmusicNews',
    publishDate: new Date('2024-04-15'),
    category: 'technology',
    readTime: 6
  },
  {
    id: '8',
    title: 'Global Music Marketing Spend Reaches $5.4 Billion',
    description: 'Labels and distributors are spending more than ever on marketing, with global music marketing expenditure reaching $5.4 billion in 2023.',
    url: 'https://musically.com/marketing-spend-music-industry-2023/',
    imageUrl: 'https://images.unsplash.com/photo-1551817958-c5b51e7b4a33?q=80&w=800',
    source: 'musicAlly',
    publishDate: new Date('2024-04-12'),
    category: 'marketing',
    readTime: 7
  },
  {
    id: '9',
    title: 'Vinyl Records Outsell CDs for Fourth Consecutive Year',
    description: 'Physical music sales continue to be dominated by vinyl, as the format outsells CDs for the fourth year in a row with 20% year-over-year growth.',
    url: 'https://www.billboard.com/business/streaming/vinyl-records-outsell-cds-fourth-year-1235729876/',
    imageUrl: 'https://images.unsplash.com/photo-1603048588665-711bd59aa33a?q=80&w=800',
    source: 'billboard',
    publishDate: new Date('2024-04-10'),
    category: 'industry',
    readTime: 4
  },
  {
    id: '10',
    title: 'Music Streaming Services Implement New Creator-Friendly Features',
    description: 'Major streaming platforms are rolling out new features designed to help artists better connect with fans and monetize their music.',
    url: 'https://www.musicbusinessworldwide.com/streaming-services-new-creator-features-12483958/',
    imageUrl: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?q=80&w=800',
    source: 'mbw',
    publishDate: new Date('2024-04-08'),
    category: 'streaming',
    readTime: 5
  },
  {
    id: '11',
    title: 'Apple Music and Spotify Raise Subscription Prices in Key Markets',
    description: 'Leading streaming services are implementing price increases in multiple markets, citing the need to better compensate artists and rights holders.',
    url: 'https://www.billboard.com/business/streaming/apple-music-spotify-price-increase-1235728765/',
    imageUrl: 'https://images.unsplash.com/photo-1524678606370-a47ad25cb82a?q=80&w=800',
    source: 'billboard',
    publishDate: new Date('2024-04-05'),
    category: 'streaming',
    readTime: 6
  },
  {
    id: '12',
    title: 'New Study Shows Social Media\'s Growing Impact on Music Discovery',
    description: 'Research reveals that over 60% of music consumers now discover new artists through social media platforms, with TikTok leading the way.',
    url: 'https://www.hypebot.com/social-media-music-discovery-study-2024/',
    imageUrl: 'https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?q=80&w=800',
    source: 'hypebot',
    publishDate: new Date('2024-04-03'),
    category: 'marketing',
    readTime: 5
  }
];
